import {
  type CatalogVariant,
  type Channel,
  type Customer,
  type Filament,
  type PackagingKit,
  type Printer,
  type Product,
  type Spool,
  type Supply,
  SUPPLY_CATEGORY_LABELS,
  SUPPLY_UNIT_LABELS,
  formatBRL,
} from '@deizi/shared';
import { useState } from 'react';
import { useCustomer, useCustomers } from '../lib/quotes-api';
import { initials } from '../lib/session';
import { Combobox } from './Combobox';
import type { ComboOption } from './Combobox';
import { Icon } from './Icon';
import { Swatch } from './Swatch';

/**
 * Combobox options per kind of record, each with its visual cue: the filament color, the product
 * cover, the customer's initials, the channel color. Call sites map their lists with these.
 */

const grams = (n: number) => `${Math.round(n).toLocaleString('pt-BR')} g`;
const qty = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

/** A small square cover (product photo), or the striped placeholder. */
export function Thumb({ url, size = 28 }: { url: string | null | undefined; size?: number }) {
  return url ? (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, borderRadius: 6, objectFit: 'cover' }}
    />
  ) : (
    <span
      className="ph"
      aria-hidden="true"
      style={{ width: size, height: size, borderRadius: 6 }}
    />
  );
}

export const filamentOption = (f: Filament): ComboOption => ({
  value: f.id,
  label: f.name,
  detail: `${f.brand} · ${f.material} · ${grams(f.stockGrams)}${f.low ? ' · estoque baixo' : ''}`,
  search: f.colorName ?? '',
  icon: <Swatch hex={f.colorHex} size={18} />,
});

/** Where a spool is: "X2D slot 2", "lacrado", "prateleira". */
function spoolWhere(s: Spool): string {
  if (s.location) {
    return `${s.location.printerName} ${s.location.slot === 'ext' ? 'externo' : `slot ${s.location.slot}`}`;
  }
  return s.status === 'sealed' ? 'lacrado' : 'prateleira';
}

export const spoolOption = (s: Spool): ComboOption => ({
  value: s.id,
  label: `${s.code} · ${grams(s.remainingGrams)}`,
  detail: `${s.filament.name} · ${spoolWhere(s)}${s.low ? ' · pouco' : ''}`,
  search: s.filament.brand,
  icon: <Swatch hex={s.filament.colorHex} size={18} />,
});

export const supplyOption = (s: Supply): ComboOption => ({
  value: s.id,
  label: s.name,
  detail: `${SUPPLY_CATEGORY_LABELS[s.category]} · tem ${qty(s.quantityOnHand)} ${SUPPLY_UNIT_LABELS[s.unit].short}${s.low ? ' · baixo' : ''}`,
  icon: <Icon name={s.category === 'packaging' ? 'box' : 'supplies'} size={18} />,
});

export const variantOption = (c: CatalogVariant): ComboOption => ({
  value: c.variant.id,
  label: `${c.product.name} · ${c.variant.name}`,
  detail: c.variant.sku,
  search: c.product.sku,
  icon: <Thumb url={c.coverUrl} />,
});

/** A variant of one product (media, listings): its color and SKU. */
export const productVariantOption = (v: {
  id: string;
  name: string;
  sku?: string;
  colorHex?: string | null;
}): ComboOption => ({
  value: v.id,
  label: v.name,
  detail: v.sku,
  icon: <Swatch hex={v.colorHex} size={18} />,
});

export const productOption = (
  p: Pick<Product, 'id' | 'name' | 'sku' | 'coverUrl'>,
): ComboOption => ({
  value: p.id,
  label: p.name,
  detail: p.sku,
  icon: <Thumb url={p.coverUrl} />,
});

export const customerOption = (
  c: Pick<Customer, 'id' | 'name' | 'whatsapp' | 'email' | 'marketplaceNickname'>,
): ComboOption => ({
  value: c.id,
  label: c.name,
  detail:
    [c.whatsapp, c.marketplaceNickname ? `@${c.marketplaceNickname}` : null, c.email]
      .filter(Boolean)
      .join(' · ') || undefined,
  icon: (
    <span className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>
      {initials(c.name)}
    </span>
  ),
});

export const channelOption = (c: Pick<Channel, 'id' | 'name' | 'color'>): ComboOption => ({
  value: c.id,
  label: c.name,
  icon: <Swatch hex={c.color} size={12} />,
});

export const kitOption = (k: PackagingKit): ComboOption => ({
  value: k.id,
  label: k.name,
  detail: formatBRL(k.totalCentavos),
  icon: <Icon name="box" size={18} />,
});

export const printerOption = (p: Pick<Printer, 'id' | 'name' | 'model'>): ComboOption => ({
  value: p.id,
  label: p.name,
  detail: p.model ?? undefined,
  icon: <Icon name="printer" size={18} />,
});

/**
 * A customer, searched on the server (name, WhatsApp, e-mail, nickname), so the list is never cut
 * at the first page. `''` means none.
 */
export function CustomerSelect({
  id,
  value,
  onChange,
  noneLabel = 'Sem cliente',
}: {
  id?: string;
  value: string;
  onChange: (id: string) => void;
  noneLabel?: string;
}) {
  const [q, setQ] = useState('');
  const results = useCustomers(q);
  const current = useCustomer(value || null);
  return (
    <Combobox
      id={id}
      value={value}
      onChange={onChange}
      onSearch={setQ}
      loading={results.isFetching}
      searchPlaceholder="Nome, WhatsApp ou apelido"
      options={[
        { value: '', label: noneLabel, pinned: 'top' },
        ...(results.data?.data ?? []).map(customerOption),
      ]}
      selected={current.data ? customerOption(current.data) : null}
    />
  );
}
